// TodayDiaryPage — read-only operational view of today's diary, grouped by
// driver. Unlike /diary/results this does not allow drag-to-reorder.
//
// API: /api/v1/diary requires (driver_id, week_start) so we cannot use it
// here. Instead we fetch /api/v1/bookings filtered to today's date and
// group by driver client-side. This trade-off is documented in
// useTodayDiary.ts.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Plus, ClipboardList } from 'lucide-react';
import { Card } from '../../components/ui/Card/Card';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column } from '../../components/ui/Table/Table';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { Tooltip } from '../../components/ui/Tooltip/Tooltip';
import { Select } from '../../components/form/Select';
import { useToast } from '../../hooks/useToast';
import { useDiaryDrivers } from './hooks/useDiaryDrivers';
import { useTodayDiary, todayISO } from './hooks/useTodayDiary';
import type { BookingStatus, BookingSummary } from '../../types/api';
import styles from './TodayDiaryPage.module.css';

const NOTE_TRUNCATE_AT = 60;

type StatusFilter = 'all' | 'in_progress' | 'pending' | 'completed';

const STATUS_FILTER_PREDICATE: Record<StatusFilter, (b: BookingSummary) => boolean> = {
  all: () => true,
  in_progress: (b) => b.status === 'in_progress',
  pending: (b) => b.status === 'pending',
  completed: (b) => b.status === 'completed',
};

const BOOKING_STATUS_VARIANTS: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancellation_requested: 'danger',
  cancelled: 'muted',
};

interface DriverGroup {
  driverId: number | null;
  driverName: string;
  bookings: BookingSummary[];
}

function groupByDriver(rows: BookingSummary[]): DriverGroup[] {
  const map = new Map<string, DriverGroup>();
  for (const b of rows) {
    const key = b.driver_id !== null ? `id:${b.driver_id}` : `name:${b.driver_name ?? 'unassigned'}`;
    const name = b.driver_name ?? 'Unassigned';
    let group = map.get(key);
    if (!group) {
      group = { driverId: b.driver_id, driverName: name, bookings: [] };
      map.set(key, group);
    }
    group.bookings.push(b);
  }
  // Sort: named drivers first by name, "Unassigned" last.
  return Array.from(map.values()).sort((a, b) => {
    if (a.driverName === 'Unassigned') return 1;
    if (b.driverName === 'Unassigned') return -1;
    return a.driverName.localeCompare(b.driverName, undefined, { sensitivity: 'base' });
  });
}

function formatToday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function truncate(text: string, n: number): string {
  return text.length <= n ? text : `${text.slice(0, n - 1).trimEnd()}…`;
}

export function TodayDiaryPage(): JSX.Element {
  const date = useMemo(() => todayISO(), []);
  const toast = useToast();
  const driversQuery = useDiaryDrivers();

  const [driverFilter, setDriverFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const driverIdFilter = driverFilter === 'all' ? null : Number(driverFilter);
  const todayQuery = useTodayDiary({ date, driverId: driverIdFilter });

  useEffect(() => {
    if (todayQuery.isError) {
      toast.error("Could not load today's diary. The shown data may be stale.");
    }
  }, [todayQuery.isError, toast]);

  const driverOptions = useMemo(
    () => [
      { value: 'all', label: 'All Drivers' },
      ...(driversQuery.data ?? []).map((d) => ({ value: String(d.id), label: d.name })),
    ],
    [driversQuery.data],
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'in_progress', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Done' },
  ];

  // Apply the status filter client-side; driver filter goes through the API.
  const filteredBookings = useMemo(
    () => (todayQuery.data ?? []).filter(STATUS_FILTER_PREDICATE[statusFilter]),
    [todayQuery.data, statusFilter],
  );

  const groups = useMemo(() => groupByDriver(filteredBookings), [filteredBookings]);

  const columns: Column<BookingSummary>[] = [
    {
      key: 'timeframe',
      header: 'Time',
      width: '90px',
      render: (_value, row) => <span className={styles.timeCell}>{row.timeframe}</span>,
    },
    { key: 'name', header: 'Customer' },
    {
      key: 'address',
      header: 'Address',
      render: (_value, row) => `${row.effective_address.address}, ${row.effective_address.postcode}`,
    },
    {
      key: 'car_make_model',
      header: 'Vehicle',
      render: (_value, row) => `${row.car_make_model} (${row.car_registration})`,
    },
    {
      key: 'status',
      header: 'Status',
      width: '160px',
      render: (_value, row) => (
        <StatusBadge status={row.status} statusMap={BOOKING_STATUS_VARIANTS} />
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (_value, row) => {
        const note = row.notes ?? '';
        if (note.length === 0) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        const shown = truncate(note, NOTE_TRUNCATE_AT);
        if (note.length <= NOTE_TRUNCATE_AT) return shown;
        return (
          <Tooltip content={note}>
            <span className={styles.notesCell} title={note}>
              {shown}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const isLoading = todayQuery.isLoading;
  const totalRows = todayQuery.data?.length ?? 0;

  return (
    <div className={styles.shell}>
      <PageHeader
        title={`Today's Diary — ${formatToday(date)}`}
        subtitle={
          <span className={styles.cardSubtitle}>{`${totalRows} booking${totalRows === 1 ? '' : 's'} across ${
            groups.length
          } driver${groups.length === 1 ? '' : 's'}`}</span>
        }
        actions={
          <Link to="/collections/diary" style={{ textDecoration: 'none' }}>
            <Button variant="secondary" icon={ChevronLeft}>
              View full diary
            </Button>
          </Link>
        }
      />

      <FilterBar ariaLabel="Today's diary filters">
        <Select
          aria-label="Driver"
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          options={driverOptions}
        />
        <Select
          aria-label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          options={statusOptions}
        />
      </FilterBar>

      {isLoading ? (
        <Card>
          <Table<BookingSummary>
            columns={columns}
            data={[]}
            keyField={(r) => r.uuid}
            loading
          />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title="No bookings scheduled for today."
            description="Once a job is booked it will appear here grouped by driver."
            action={
              <Link to="/collections/bookings/create" style={{ textDecoration: 'none' }}>
                <Button variant="primary" icon={Plus}>
                  Book a job
                </Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className={styles.driverCards}>
          {groups.map((group) => (
            <Card
              key={group.driverName}
              title={
                <div className={styles.driverCardTitle}>
                  <span>{group.driverName}</span>
                  <span className={styles.bookingCount}>
                    {group.bookings.length}
                    {group.bookings.length === 1 ? ' booking' : ' bookings'}
                  </span>
                </div>
              }
              flush
            >
              <div className={styles.tableWrap}>
                <Table<BookingSummary>
                  columns={columns}
                  data={group.bookings}
                  keyField={(r) => r.uuid}
                  ariaLabel={`Bookings for ${group.driverName}`}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
